import LegalPage from "@/components/LegalPage";

const sections = [
  {
    id: "overview",
    title: "1. Overview",
    body: (
      <>
        <p>
          This Privacy Policy describes how <strong>Ashren Consulting Ltd.</strong> (&ldquo;Ashren,&rdquo;
          &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) collects, uses, discloses, and
          safeguards personal information in connection with our website
          (<a href="https://www.ashrenltd.com" className="underline">www.ashrenltd.com</a>) and the
          staffing, consulting, and lead-magnet services we provide to clients and candidates
          across Canada.
        </p>
        <p>
          We are accountable for the personal information in our custody and control and comply
          with the federal <em>Personal Information Protection and Electronic Documents Act</em>
          {" "}(PIPEDA), the <em>British Columbia Personal Information Protection Act</em> (PIPA BC),
          and Quebec&rsquo;s <em>Act respecting the protection of personal information in the
          private sector</em> (Law 25) where applicable.
        </p>
      </>
    ),
  },
  {
    id: "information-we-collect",
    title: "2. Information we collect",
    body: (
      <>
        <p>We collect the following categories of personal information:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Employer intake information</strong> — company name, contact name, work email,
            phone, province, engagement model, urgency, role description, and any additional
            context you provide when you request a talent brief.
          </li>
          <li>
            <strong>Candidate profile information</strong> — full name, email, phone, current role,
            years of experience, specialization, preferred province, resume/LinkedIn URLs, and a
            short summary about the opportunities you&rsquo;re seeking.
          </li>
          <li>
            <strong>General contact information</strong> — name, email, phone (optional), subject,
            inquiry type, and the content of your message.
          </li>
          <li>
            <strong>Lead-magnet email capture</strong> — name (optional), work email, company
            (optional), and which resource you requested (Canada Tech Salary Guide, AI Talent
            Readiness Assessment, or Staffing Vendor Audit Checklist).
          </li>
          <li>
            <strong>Technical information</strong> — IP address, browser type, device type, pages
            viewed, referring URL, and aggregated usage analytics collected via first- and
            third-party tools (see our <a href="/cookies" className="underline">Cookie Policy</a>).
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "3. How we use your information",
    body: (
      <>
        <p>We use personal information only for the purposes for which it was collected, including:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Responding to inquiries and providing the staffing services you request.</li>
          <li>Matching candidates to appropriate roles and representing candidates to client organizations (always with explicit consent before a profile is shared).</li>
          <li>Delivering the lead-magnet PDFs you request and sending a single follow-up if appropriate.</li>
          <li>Improving our website, content, and services through aggregated analytics.</li>
          <li>Meeting legal, regulatory, audit, insurance, and risk-management obligations.</li>
        </ul>
        <p>
          We do not sell personal information. We do not use personal information for
          profiling or automated decision-making that produces legal effects about you.
        </p>
      </>
    ),
  },
  {
    id: "legal-basis",
    title: "4. Legal basis and consent",
    body: (
      <>
        <p>
          We rely on your <strong>consent</strong> — which you provide when you submit a form on
          our website or otherwise engage our services — as the primary legal basis for processing
          personal information. Where permitted by applicable law, we may also rely on legitimate
          business interests (e.g., record keeping, fraud prevention) or compliance with a legal
          obligation.
        </p>
        <p>
          You may withdraw consent at any time by emailing{" "}
          <a href="mailto:Sales@ashrenltd.com" className="underline">Sales@ashrenltd.com</a>.
          Withdrawal will not affect processing carried out before withdrawal.
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "5. How we share information",
    body: (
      <>
        <p>We share personal information only in the following circumstances:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>With client organizations</strong> — candidate profiles are only shared with
            specific client employers after we obtain your explicit consent to represent you for a
            specific role.
          </li>
          <li>
            <strong>With service providers</strong> — trusted third-party vendors who help us
            operate our business, such as our website host (Emergent), database (MongoDB),
            transactional email provider (Resend), and analytics provider (PostHog). These
            providers are bound by written agreements to protect personal information and use it
            only for the purposes we specify.
          </li>
          <li>
            <strong>For legal reasons</strong> — where required to comply with valid legal
            process, protect our rights, or prevent fraud or harm.
          </li>
          <li>
            <strong>In a business transaction</strong> — if Ashren is involved in a merger,
            acquisition, financing, or sale of assets, personal information may be transferred to
            the relevant counterparty subject to appropriate confidentiality undertakings.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "cross-border",
    title: "6. Cross-border storage and processing",
    body: (
      <>
        <p>
          Some of our service providers operate outside of Canada, including in the United States
          and the European Union. When personal information is transferred outside Canada, it may
          be accessible to foreign governments, courts, and law-enforcement agencies under the
          laws of those jurisdictions.
        </p>
        <p>
          We use contractual and technical safeguards to ensure a comparable level of protection
          for personal information that is transferred outside Canada.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "7. Retention",
    body: (
      <>
        <p>
          We retain personal information only as long as needed to fulfil the purposes set out in
          this policy and to meet legal and accounting obligations. Typical retention periods:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Employer intakes: 24 months after last engagement, then anonymized or deleted.</li>
          <li>Candidate profiles: 36 months from last interaction, unless you ask us to delete sooner.</li>
          <li>Contact-form messages: 24 months after response.</li>
          <li>Lead-magnet captures: 24 months or until you unsubscribe.</li>
          <li>Tax, audit, and insurance records: 7 years as required by Canadian law.</li>
        </ul>
      </>
    ),
  },
  {
    id: "security",
    title: "8. Security safeguards",
    body: (
      <>
        <p>
          We use a combination of physical, organizational, and technical safeguards to protect
          personal information against loss, theft, unauthorized access, disclosure, copying, use
          or modification. Safeguards include encryption in transit (TLS 1.2+), access controls,
          least-privilege data access for staff, vendor due diligence, and breach-notification
          procedures.
        </p>
        <p>
          No method of transmission or storage is 100% secure. If you have reason to believe your
          interaction with us is no longer secure, notify us immediately at{" "}
          <a href="mailto:Sales@ashrenltd.com" className="underline">Sales@ashrenltd.com</a>.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "9. Your rights",
    body: (
      <>
        <p>Under Canadian privacy law, you have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Access the personal information we hold about you;</li>
          <li>Request correction of inaccurate or incomplete information;</li>
          <li>Withdraw consent to our use or disclosure of your information (subject to legal and contractual restrictions);</li>
          <li>Request deletion of your personal information where we no longer need it;</li>
          <li>Lodge a complaint with the Office of the Privacy Commissioner of Canada, the Information and Privacy Commissioner for British Columbia, or the Commission d&rsquo;acc&egrave;s &agrave; l&rsquo;information du Qu&eacute;bec.</li>
        </ul>
        <p>
          Exercise any of these rights by emailing{" "}
          <a href="mailto:Sales@ashrenltd.com" className="underline">Sales@ashrenltd.com</a>.
          We will respond within 30 days and may request reasonable proof of identity before
          releasing or modifying information.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "10. Children",
    body: (
      <>
        <p>
          Our services are directed to working professionals and are not intended for individuals
          under the age of 18. We do not knowingly collect personal information from children. If
          you believe we have inadvertently collected such information, contact us and we will
          delete it.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "11. Changes to this policy",
    body: (
      <>
        <p>
          We may update this Privacy Policy from time to time. The effective date at the top of
          this page indicates when it was last revised. Material changes will be communicated via
          a prominent notice on our website or by email where you have provided one.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "12. Contact our Privacy Officer",
    body: (
      <>
        <p>
          Questions, complaints, or requests related to this Privacy Policy should be directed to
          our Privacy Officer:
        </p>
        <p>
          <strong>Ashren Consulting Ltd. — Privacy Officer</strong><br />
          101, 2529 Kingsway<br />
          Vancouver, BC V5R 5H3<br />
          Canada<br />
          Email: <a href="mailto:Sales@ashrenltd.com" className="underline">Sales@ashrenltd.com</a><br />
          Phone: <a href="tel:+12364850134" className="underline">+1 236-485-0134</a>
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalPage
      eyebrow="Legal · Privacy"
      title="Privacy Policy"
      subtitle="How Ashren Consulting Ltd. collects, uses, and protects your personal information — in line with PIPEDA, BC PIPA, and Quebec Law 25."
      effectiveDate="May 4, 2026"
      sections={sections}
      testId="privacy-policy-page"
    />
  );
}
